import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Calculator, Upload, FileText, File, FileSpreadsheet, Download, Trash2, Pencil, Check, Shield, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILE_ICONS = {
  pdf: { icon: FileText, color: 'text-red-400', bg: 'bg-red-900/30' },
  docx: { icon: File, color: 'text-blue-400', bg: 'bg-blue-900/30' },
  doc: { icon: File, color: 'text-blue-400', bg: 'bg-blue-900/30' },
  xlsx: { icon: FileSpreadsheet, color: 'text-green-400', bg: 'bg-green-900/30' },
  xls: { icon: FileSpreadsheet, color: 'text-green-400', bg: 'bg-green-900/30' },
  csv: { icon: FileSpreadsheet, color: 'text-emerald-400', bg: 'bg-emerald-900/30' },
};
const formatSize = (b) => !b ? '-' : b < 1024 ? b + ' B' : b < 1048576 ? (b / 1024).toFixed(1) + ' KB' : (b / 1048576).toFixed(1) + ' MB';

export default function ISYMukayeseliKesif({ ihaleler }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analiz, setAnaliz] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editAciklama, setEditAciklama] = useState('');
  const fileInputRef = useRef(null);

  const fetchData = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/isy/kesif/${id}`, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchAnaliz = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/isy/mevzuat-analiz/${id}`, { withCredentials: true });
      if (res.data?.length > 0) setAnaliz(res.data[0]);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (selectedIhale) { fetchData(selectedIhale.id); fetchAnaliz(selectedIhale.id); } }, [selectedIhale, fetchData, fetchAnaliz]);

  const triggerAnaliz = async (oskaId) => {
    setAnalyzing(true);
    try {
      const res = await axios.post(`${API}/isy/mevzuat-analiz/${selectedIhale.id}`, { oska_id: oskaId, kaynak: 'kesif' }, { withCredentials: true });
      setAnaliz(res.data);
      toast.success('Mevzuat analizi tamamlandı');
    } catch (err) { toast.error(err.response?.data?.detail || 'Analiz hatası'); }
    finally { setAnalyzing(false); }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    let lastOskaId = null;
    let success = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/isy/kesif/${selectedIhale.id}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
        success++;
        if (res.data?.oska_id) lastOskaId = res.data.oska_id;
      } catch (err) { toast.error(`${file.name}: ${err.response?.data?.detail || 'Yükleme hatası'}`); }
    }
    if (success > 0) {
      toast.success(`${success} dosya yüklendi`);
      fetchData(selectedIhale.id);
      if (lastOskaId) triggerAnaliz(lastOskaId);
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/kesif/item/${deleteId}`, { withCredentials: true });
      toast.success('Silindi'); setDeleteId(null); fetchData(selectedIhale.id);
    } catch (e) { toast.error('Silme hatası'); }
  };

  const handleSaveAciklama = async (id) => {
    try {
      await axios.put(`${API}/isy/kesif/item/${id}`, { aciklama: editAciklama }, { withCredentials: true });
      toast.success('Güncellendi'); setEditId(null); fetchData(selectedIhale.id);
    } catch (e) { toast.error('Hata'); }
  };

  if (!selectedIhale) {
    return (
      <div className="space-y-4" data-testid="isy-kesif">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-bold text-white">Mukayeseli Keşif</h2>
        </div>
        <p className="text-sm text-gray-400">Keşif belgelerini yüklemek için bir ihale seçin:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(ihaleler || []).map(ih => (
            <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-violet-500/40 cursor-pointer transition-colors" onClick={() => setSelectedIhale(ih)} data-testid={`kesif-select-${ih.id}`}>
              <CardContent className="py-3 px-4">
                <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                <p className="text-xs text-gray-500 mt-1">IKN: {ih.ikn || '-'} | {ih.sozlesme_turu || '-'}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const riskColor = analiz?.analiz?.risk_level === 'kritik' || analiz?.analiz?.risk_level === 'yuksek' ? 'border-red-700/50 bg-red-900/10' : analiz?.analiz?.risk_level === 'orta' ? 'border-amber-700/50 bg-amber-900/10' : analiz?.analiz ? 'border-green-700/50 bg-green-900/10' : '';

  return (
    <div className="space-y-4" data-testid="isy-kesif">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIhale(null)} className="text-gray-400 hover:text-white h-8 px-2"><ArrowLeft className="w-4 h-4 mr-1" /> Geri</Button>
          <div>
            <h2 className="text-sm font-bold text-white">{selectedIhale.is_adi}</h2>
            <p className="text-xs text-gray-500">Mukayeseli Keşif Belgeleri</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-gray-800 text-gray-300 text-xs">{items.length} belge</Badge>
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx" className="hidden" onChange={handleUpload} />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} size="sm" className="bg-violet-700 hover:bg-violet-600 text-white" data-testid="kesif-upload-btn">
            <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Yükleniyor...' : 'Belge Yükle'}
          </Button>
        </div>
      </div>

      <Card className="bg-gray-800/30 border-gray-700/40 border-dashed border-2 hover:border-violet-600/50 transition-colors cursor-pointer" onClick={() => fileInputRef.current?.click()}>
        <CardContent className="py-8 text-center">
          <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Excel, CSV, PDF veya Word dosyalarını yükleyin</p>
          <p className="text-xs text-gray-600 mt-1">Excel/CSV dosyaları otomatik olarak mevzuat denetimine tabi tutulur</p>
        </CardContent>
      </Card>

      {/* Mevzuat Analiz Sonucu */}
      {(analyzing || analiz?.analiz) && (
        <Card className={`border ${analyzing ? 'bg-blue-900/15 border-blue-700/50 animate-pulse' : riskColor}`}>
          <CardContent className="py-3">
            {analyzing ? (
              <div className="flex items-center gap-3 justify-center py-2">
                <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                <div><p className="text-sm font-semibold text-blue-300">Mevzuat Analizi Yapılıyor...</p><p className="text-xs text-gray-500">YİGŞ Md.21/22, 4735 Md.15</p></div>
              </div>
            ) : analiz?.analiz ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-cyan-400" /><span className="text-sm font-bold text-white">Mevzuat Denetim Sonucu</span></div>
                  <Badge className={`text-xs ${analiz.analiz.risk_level === 'kritik' || analiz.analiz.risk_level === 'yuksek' ? 'bg-red-700' : analiz.analiz.risk_level === 'orta' ? 'bg-amber-600' : 'bg-green-700'} text-white`}>
                    {analiz.analiz.risk_level?.toUpperCase() || 'BELİRSİZ'} RİSK
                  </Badge>
                </div>
                {analiz.analiz.ozet && <p className="text-xs text-gray-300 bg-gray-800/50 p-2 rounded">{analiz.analiz.ozet}</p>}
                {(analiz.analiz.warnings || []).slice(0, 5).map((w, i) => (
                  <div key={i} className={`p-2 rounded text-xs flex gap-2 ${w.severity === 'kritik' || w.severity === 'yuksek' ? 'bg-red-900/20 border border-red-800/30' : 'bg-gray-800/50 border border-gray-700/30'}`}>
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-400" />
                    <div><p className="text-gray-200">{w.message}</p>{w.madde_ref && <span className="text-blue-400 text-[10px]">{w.madde_ref}</span>}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* Dosya listesi */}
      {loading ? <div className="text-center text-gray-400 py-8">Yükleniyor...</div> : items.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700/50"><CardContent className="py-12 text-center text-gray-500">Henüz belge yüklenmemiş.</CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => {
            const fcfg = FILE_ICONS[item.dosya_tipi] || FILE_ICONS.pdf;
            const FIcon = fcfg.icon;
            const hasData = !!item.parsed_data;
            return (
              <Card key={item.id} className="bg-gray-900/60 border-gray-700/50 hover:border-gray-600/50 transition-colors">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${fcfg.bg} flex items-center justify-center flex-shrink-0`}><FIcon className={`w-5 h-5 ${fcfg.color}`} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.dosya_adi}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                        <span>{formatSize(item.dosya_boyutu)}</span>
                        <Badge className="bg-gray-800 text-gray-400 text-[10px] uppercase">{item.dosya_tipi}</Badge>
                        {hasData && <Badge className="bg-violet-900/40 text-violet-300 text-[10px]">{item.parsed_data.satir_sayisi} satır</Badge>}
                        <span>{item.yukleyen}</span>
                        <span>{item.created_at ? new Date(item.created_at).toLocaleDateString('tr-TR') : ''}</span>
                      </div>
                      {editId === item.id ? (
                        <div className="flex items-center gap-2 mt-1.5">
                          <Input value={editAciklama} onChange={e => setEditAciklama(e.target.value)} className="bg-gray-800 border-gray-700 text-white h-7 text-xs flex-1" placeholder="Açıklama..." />
                          <Button size="sm" onClick={() => handleSaveAciklama(item.id)} className="h-7 w-7 p-0 bg-green-700"><Check className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditId(null)} className="h-7 text-xs text-gray-400">İptal</Button>
                        </div>
                      ) : item.aciklama ? <p className="text-[11px] text-gray-400 mt-1 italic">{item.aciklama}</p> : null}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {hasData && <Button variant="ghost" size="sm" className="h-7 px-1.5 text-gray-400 hover:text-cyan-400" onClick={() => triggerAnaliz(item.oska_id || null)} disabled={analyzing} title="Mevzuat Analizi"><Shield className="w-3.5 h-3.5" /></Button>}
                      <a href={`${API}/isy/dosya/${item.dosya_yolu}`} target="_blank" rel="noopener noreferrer" download><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-violet-400"><Download className="w-3.5 h-3.5" /></Button></a>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-blue-400" onClick={() => { setEditId(item.id); setEditAciklama(item.aciklama || ''); }}><Pencil className="w-3 h-3" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(item.id)}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => { if (!o) setDeleteId(null); }}>
        <AlertDialogContent className="bg-gray-900 border-gray-700"><AlertDialogHeader><AlertDialogTitle className="text-white">Belgeyi silmek istediğinize emin misiniz?</AlertDialogTitle><AlertDialogDescription className="text-gray-400">Dosya kalıcı olarak silinecektir.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel className="bg-gray-800 border-gray-700 text-white">İptal</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-700 text-white">Sil</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
